/**
 * useDashboardUser — персональные данные дашборда.
 *
 * GET /api/luna/me/dashboard (authenticated)
 *
 * Реактивность:
 *   1. Initial fetch при mount.
 *   2. SSE-подписка на /me/stream — push-сигнал «changed» от backend
 *      → fetchData() мгновенно. Срабатывает на любое изменение состояния
 *      юзера: депозит, админ-кредит, claim, покупка ноды, реф-доход и т.д.
 *   3. Fallback-поллинг раз в 5 минут — на случай если SSE оборвётся
 *      между keep-alive и до auto-reconnect EventSource'а.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, lunaApi, getAuthToken } from '@/services/api';
import type { BusinessRow } from '@/components/nodes/BusinessActivity';

export interface DashboardUserData {
  balance: string;
  todayReward: number;
  todayChangePct: number;
  /** Прирост доходности в % по прошедшему времени МСК-дня */
  todayAccruedPct?: number;
  /** Юзеров онлайн (active < 600 сек) */
  onlineCount?: number;
  month30Reward: number;
  totalLps: number;
  contractName: string | null;
  contractMonthly: string | null;
  contractProgress: number;
  activeContractsCount: number;
  slotsSummary: Array<{ name: string; count: number; totalInvested: number }>;
  refIncome: number;
  refLinesOpen: number;
  pendingProfit: number;
  /** Только non-PC ноды — именно эту сумму зачислит ручной Claim.
   *  Используется в ClaimModal / ClaimStatusPill. */
  manualPendingProfit: number;
  /** Сумма PC-авто-клеймов с момента последнего ручного клейма юзера.
   *  Используется чтобы счётчик «Генерация прибыли» не откатывался
   *  вниз когда один из PC-слотов авто-клеймится: pending этого слота
   *  обнуляется, но cycleAccumulated растёт на ту же сумму. */
  cycleAccumulated: number;
  cycleSecondsLeft: number;
  claimReady: boolean;
  plasmaCoreActive: boolean;
  plasmaCoreUntil: number | null;
  pcAvgChargePct: number | null;
  pcContractsCount: number;
  manualContractsCount: number;
  pcNextClaimMs: number | null;
  pcLifetimeEarned: number;
  /** Детальный список PC-нод для инфо-модалки автосбора. */
  pcContracts: Array<{
    contractId: string;
    slotName: string;
    pricePaid: number;
    pending: number;
  }>;
  businesses: BusinessRow[];
  feed: Array<{
    kind: 'passive' | 'ref' | 'status' | 'spend' | 'topup';
    title: string;
    subtitle: string;
    amount: number;
  }>;
}

// SSE — основной источник реактивных обновлений. Поллинг — только
// fallback на случай разрыва соединения. 5 мин достаточно: cycle
// нодов 24ч, ничего срочного не теряется между SSE-пингами.
const POLL_MS = 5 * 60_000;

export function useDashboardUser(): DashboardUserData | null;
export function useDashboardUser(
  opts: { withRefetch: true; paused?: boolean },
): [DashboardUserData | null, () => Promise<void>];
export function useDashboardUser(
  opts?: { withRefetch?: boolean; paused?: boolean },
): DashboardUserData | null | [DashboardUserData | null, () => Promise<void>] {
  const [data, setData] = useState<DashboardUserData | null>(null);
  const mountedRef = useRef(true);
  const paused = opts?.paused ?? false;

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data?: DashboardUserData }>(
        '/me/dashboard',
      );
      if (!mountedRef.current) return;
      if (res.data.success && res.data.data) {
        setData(res.data.data);
      }
    } catch {
      /* Тихо — следующий тик повторит */
    }
  }, []);

  // Первая загрузка — всегда. Полинг — только если не paused.
  // paused=true ставится когда юзер открыл модалку: refetch обновляет
  // apiData → новая ссылка на pcContracts/etc → пересборка motion-
  // компонентов в открытой модалке → визуальное «моргание» на iOS.
  useEffect(() => {
    mountedRef.current = true;
    void fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(fetchData, POLL_MS);
    return () => window.clearInterval(id);
  }, [paused, fetchData]);

  // SSE-подписка — push реактивные обновления. На каждый сигнал
  // 'changed' от бэка делаем fetchData() с DEBOUNCE 600ms.
  //
  // Зачем debounce: одна операция (например активация Plasma Core)
  // может эмитить несколько `changed` подряд (изменился баланс,
  // изменились контракты, реф-выплаты и т.д.). Без debounce
  // получаем burst re-renders → каскад remount'ов в дереве
  // (BatteryWidget → AppHeader → дочерние страницы) → анимации
  // мерцают / стакаются. С debounce — один refetch после паузы.
  useEffect(() => {
    if (paused) return;
    const token = getAuthToken();
    if (!token) return;

    let es: EventSource | null = null;
    let debounceTimer: number | null = null;
    const scheduleFetch = (): void => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        if (!mountedRef.current) return;
        void fetchData();
      }, 600);
    };
    try {
      es = new EventSource(lunaApi.user.streamUrl(token));
      es.addEventListener('changed', () => {
        if (!mountedRef.current) return;
        scheduleFetch();
      });
      es.addEventListener('error', () => {
        // EventSource сам переподключится. Не шумим.
      });
    } catch {
      /* SSE не поддерживается — останется только fallback-поллинг */
    }

    return () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      if (es) es.close();
    };
  }, [paused, fetchData]);

  if (opts?.withRefetch) return [data, fetchData];
  return data;
}
